// src/services/api.js
import axios from 'axios';

const baseURL = process.env.NODE_ENV === 'production' 
  ? 'https://stray-back.onrender.com/api'  
  : 'http://localhost:3000/api';


// Create axios instance with custom config
const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Important for CORS with credentials
});


// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If refresh token exists, try to refresh access token
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { token } = response.data;
          
          localStorage.setItem('token', token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          
          return api(originalRequest);
        } catch (refreshError) {
          // If refresh fails, logout user
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);
//Game data

const gameData = [
  {
    id: 1,
    category: "Puzzle",
    games: [
      {
        id: "1.1",
        title: "2048",
        description: "Slide numbered tiles to combine them and reach 2048.",
        difficulty: "Medium",
        popularity: "High",
        thumbnailUrl: "/public/static/placeholder/game.png",
        premium: false
      },
      {
        id: "1.2",
        title: "MemoryMatch",
        description: "Test your memory by matching pairs of cards with various themes",
        difficulty: "Easy",
        popularity: "High",
        thumbnailUrl: "/public/static/placeholder/memory.png",
        premium: false
      },
      {
        id: "1.3",
        title: "XOGame",
        description: "Test out playing TicTacToe with AI",
        difficulty: "Easy",
        popularity: "High",
        thumbnailUrl: "/public/static/placeholder/memory.png",
        premium: false
      },
      {
        id: "1.4",
        title: "Sudoku",
        description: "Test out playing Sudoku with AI",
        difficulty: "Easy",
        popularity: "High",
        thumbnailUrl: "/public/static/placeholder/memory.png",
        premium: false
      },
      {
        id: "1.5",
        title: "Chess",
        description: "Classic strategy board game",
        difficulty: "Hard",
        popularity: "High",
        thumbnailUrl: "/public/static/placeholder/memory.png",
        premium: false
      },
// Add other games from your list...
    ]
  }
  // Add other categories...
];

// Auth endpoints
const auth = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword })
};

// Games endpoints
const games = {
  getAllGames: () => Promise.resolve({ data: gameData.flatMap(cat => cat.games) }),
  getGamesByCategory: () => Promise.resolve({ data: gameData }),getAllGames: () => api.get('/games'),
  getGameById: (id) => api.get(`/games/${id}`),
  getGameStatus: (id) => api.get(`/games/${id}/status`),
  getFeaturedGames: () => api.get('/games/featured'),
  searchGames: (query) => api.get('/games/search', { params: { q: query } })
};

// Streaming endpoints
const streaming = {
  startSession: (gameId) => api.post('/streaming/start', { gameId }),
  endSession: (sessionId) => api.post('/streaming/end', { sessionId }),
  getSessionInfo: (sessionId) => api.get(`/streaming/session/${sessionId}`),
  heartbeat: (sessionId) => api.post(`/streaming/heartbeat`, { sessionId }),
  getServerStatus: () => api.get('/streaming/status')
};

// Subscription endpoints
const subscription = {
  createCheckoutSession: (priceId) => api.post('/subscription/create-checkout-session', { priceId }),
  getCurrentPlan: () => api.get('/subscription/current')
};

// User profile endpoints
const profile = {
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateAvatar: (formData) => api.put('/profile/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getGameHistory: () => api.get('/profile/game-history')
};


// User activity endpoints
const activity = {
  getRecentGames: () => api.get('/activity/recent-games'),
  getPlayHistory: () => api.get('/activity/play-history'),
  getAchievements: () => api.get('/activity/achievements')
};

const session = {
  getActiveSessions: () => api.get('/sessions'),
  revokeSession: (sessionId) => api.delete(`/sessions/${sessionId}`),
  revokeAllSessions: () => api.delete('/sessions/all')
};

// Stats and metrics endpoints
const metrics = {
  getPlayTime: () => api.get('/metrics/playtime'),
  getPerformanceMetrics: (sessionId) => api.get(`/metrics/performance/${sessionId}`),
  reportIssue: (data) => api.post('/metrics/report-issue', data),
  getBandwidthUsage: () => api.get('/metrics/bandwidth')
};

export {
  api as default,
  auth,
  games,
  streaming,
  subscription,
  profile,
  metrics
};